/**
 * <flame-hero>
 *
 * Cinematic landing hero. Spawns rising ember particles into [data-embers] and applies a
 * light parallax to the background image/video and the flames on scroll. All motion is
 * disabled under prefers-reduced-motion (no embers, no parallax). No imports — matches the
 * plain custom-element pattern used by assets/youtube-wall.js.
 */
const FH_REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class FlameHero extends HTMLElement {
  #embersHost = null;
  #bg = null;
  #flames = null;
  #onScroll = null;
  #raf = 0;
  #io = null;

  connectedCallback() {
    this.#embersHost = this.querySelector('[data-embers]');
    this.#bg = this.querySelector('[data-flame-bg]');
    this.#flames = this.querySelector('[data-flames]');

    if (FH_REDUCE_MOTION.matches) return;

    this.#spawnEmbers();

    // Only run the scroll/parallax loop while the hero is on screen.
    this.#io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this.#start();
        else this.#stop();
      },
      { threshold: 0 }
    );
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#io?.disconnect();
  }

  #spawnEmbers() {
    if (!this.#embersHost) return;
    const requested = parseInt(this.dataset.emberCount || '18', 10);
    const count = Math.max(0, Math.min(Number.isFinite(requested) ? requested : 18, 40));
    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const ember = document.createElement('span');
      ember.className = 'flame-hero__ember';
      ember.style.setProperty('--x', `${(Math.random() * 100).toFixed(2)}%`);
      ember.style.setProperty('--size', `${(Math.random() * 5 + 2).toFixed(1)}px`);
      ember.style.setProperty('--dur', `${(Math.random() * 4 + 4).toFixed(2)}s`);
      ember.style.setProperty('--delay', `${(-Math.random() * 8).toFixed(2)}s`);
      ember.style.setProperty('--drift', `${(Math.random() * 80 - 40).toFixed(0)}px`);
      ember.style.setProperty('--o', `${(Math.random() * 0.5 + 0.4).toFixed(2)}`);
      frag.appendChild(ember);
    }
    this.#embersHost.appendChild(frag);
  }

  #start() {
    if (!this.#onScroll) {
      this.#onScroll = () => {
        if (!this.#raf) this.#raf = requestAnimationFrame(this.#update);
      };
    }
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#raf = requestAnimationFrame(this.#update);
  }

  #stop() {
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  #update = () => {
    this.#raf = 0;
    const top = this.getBoundingClientRect().top;
    const scrolled = Math.max(0, -top);
    const bgShift = scrolled * 0.35; // background drifts slower (depth)
    const fgShift = scrolled * 0.12;
    if (this.#bg) this.#bg.style.transform = `translate3d(0, ${bgShift}px, 0) scale(1.12)`;
    if (this.#flames) this.#flames.style.transform = `translate3d(0, ${fgShift}px, 0)`;
    if (this.#embersHost) this.#embersHost.style.transform = `translate3d(0, ${fgShift * 0.6}px, 0)`;
  };
}

if (!customElements.get('flame-hero')) {
  customElements.define('flame-hero', FlameHero);
}
